import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import useApi from "../utils/useApi";
import { useLoading } from "../context/LoadingContext";
import BasicInfoBox from "../components/BasicInfoBox";
import UserSettings from "../components/UserSettings";
import Toaster, { ToastContainerComponent } from "../components/Toaster";
import { Modal } from "bootstrap";
import { useAuth } from "../context/AuthContext";

const CreateClients = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const { user } = useAuth();

  const [searchFolderQuery, setSearchFolderQuery] = useState("");
  const [allFolders, setAllFolders] = useState([]);
  const [tempSelectedFolders, setTempSelectedFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDetails, setNewFolderDetails] = useState("");
  const [assignClientToFolder, setAssignClientToFolder] = useState("yes");

  const [basicInfoList, setBasicInfoList] = useState([
    { id: 1, user_name: "", user_email: "", user_password: "", user_company: "" }
  ]);

  const [userSettings, setUserSettings] = useState({
    changePassword: false,
    accessSettings: false,
  });

  // **Add New Basic Info Row**
  const addNewBasicInfo = () => {
    setBasicInfoList([
      ...basicInfoList,
      { id: Date.now(), user_name: "", user_email: "", user_password: "", user_company: "" }
    ]);
  };

  // **Delete Basic Info Row**
  const deleteBasicInfo = (id) => {
    if (basicInfoList.length > 1) {
      setBasicInfoList(basicInfoList.filter((info) => info.id !== id));
    }
  };

  // **Handle Input Change for Each Row**
  const handleInputChange = (id, field, value) => {
    if (field === "user_name") {
      // const isValid = /^[a-zA-Z\s]*$/.test(value); // allows only letters and spaces
      if (!/^[a-zA-Z\s]*$/.test(value)) {
        Toaster.error("Name should contain only alphabets.");
        return; // stop further update
      }
    }
    console.log(`Updating ${field} for ID ${id} with value:`, value); // ✅ Debug log
    const updatedList = basicInfoList.map((info) =>
      info.id === id ? { ...info, [field]: value } : info
    );
    setBasicInfoList(updatedList);
  };

  // **Handle User Settings Change**
  const handleUserSettingsChange = (field, value) => {
    setUserSettings({ ...userSettings, [field]: value });
  };

  // **Handle Form Submission**
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const requestData = basicInfoList.map((user) => ({
      user_name: user.user_name,
      user_email: user.user_email,
      user_password: user.user_password,
      user_company: user.user_company,
      user_role: "CLIENT",
      user_type: "CLIENT",
      access_user_settings: userSettings.accessSettings ?? false,
      change_password: userSettings.changePassword ?? false,
    }));

    console.log("requestData", requestData);

    // Validation: Ensure all fields are filled
    if (requestData.some(user => !user.user_email || !user.user_password)) {
      Toaster.error("Please provide email & password.");
      setLoading(false);
      return;
    }

    try {
      const response = await callApi({
        endpoint: "admin/clients/store",
        method: "POST",
        data: requestData,
      });

      if (response?.success) {
        const { users = [], errors = [] } = response?.data || {};

        if (users.length > 0) {
          Toaster.success(`${users.length}/${basicInfoList.length} clients created successfully!`);
        }

        if (errors.length > 0) {
          Toaster.error(`${errors.length}/${basicInfoList.length} emails already exist.`);

          // Store errors in state mapped by email
          const errorMap = errors.reduce((acc, err) => {
            acc[err.user_email] = err.errors; // Storing email-specific error message
            return acc;
          }, {});
          setErrors(errorMap);
        }

        // Remove successfully added users
        const failedUsers = basicInfoList.filter(user =>
          errors.some(error => error.user_email === user.user_email)
        );
        setBasicInfoList(failedUsers);

        console.log("response", response);
        console.log("User ID:",);


        // Redirect if all users were added successfully
        if (selectedFolders.length > 0 && basicInfoList.length === 1) {
          assignFolderTouser(selectedFolders.map(folder => folder.entity_id), response.data.users[0].user_id);
        }
        if (failedUsers.length === 0) {
          setTimeout(() => navigate("/browse-clients"), 1500);
        }
      }

    } catch (error) {
      const delay = 2000;
      if (error?.response?.status === 422) {
        error.response.data.data.errors.forEach((err, index) => {
          setTimeout(() => {
            Toaster.error(`${err.user_email}: ${err.errors}`);
          }, index * delay);
        });
      } else if (error?.response) {
        // API responded with an error status
        const errorMessage = error.response?.data?.message || "An error occurred while creating the clients.";
        Toaster.error(errorMessage);
      } else if (error?.request) {
        // Request was made but no response received (backend down, network error, etc.)
        Toaster.error("Cannot connect to server. Please check if the backend is running.");
      } else {
        // Something else happened
        Toaster.error("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const openFolderModal = () => {
    setTempSelectedFolders([...selectedFolders]); // Copy current selections to temp

    const modalElement = document.getElementById("addFolderModal");
    const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };

  const openAddFolderModal = () => {

    const modalElement = document.getElementById("createFolder");
    const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };


  const fetchFolders = async (keyword) => {
    if (keyword.length < 3) {
      setAllFolders([]); // Clear list if query is less than 3 characters
      return;
    }

    setIsSearching(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/get-sub-directory",
        params: { keyword }, // Pass keyword for search
      });

      if (response?.success) {
        setAllFolders(response?.data?.directories || []);
      } else {
        Toaster.error("Failed to load directories.");
      }
    } catch (error) {
      console.error("Error fetching directories:", error);
      Toaster.error("Error fetching directories.");
    } finally {
      setIsSearching(false);
    }
  };

  const toggleTempFolderSelection = (folder) => {
    setTempSelectedFolders((prevFolders) => {
      return prevFolders.some((f) => f.entity_id === folder.entity_id)
        ? prevFolders.filter((f) => f.entity_id !== folder.entity_id) // Remove user
        : [...prevFolders, folder]; // Add user
    });
  };

  // ✅ Assign Users from Modal to Main List
  const handleAssignFolders = () => {
    setSelectedFolders(tempSelectedFolders); // Update assigned users
    // Close modal
    const modalElement = document.getElementById("addFolderModal");
    const modalInstance = Modal.getInstance(modalElement);
    modalInstance.hide();
  };

  // ✅ Remove Assigned User
  const removeAssignedFolder = (entityId) => {
    setSelectedFolders((prevFolders) => {
      const updatedUsers = prevFolders.filter((f) => f.entity_id !== entityId);
      return updatedUsers;
    });
  };

  const assignFolderTouser = async (entity_ids, userid) => {
    setLoading(true);
    try {
      const data = {
        directory_ids: entity_ids,
        user_ids: [userid]
      };
      const response = await callApi({
        endpoint: "/admin/directories/add-directory-asigned-user",
        method: "POST",
        data: data,
      });

      //console.log("response", response.data.data);

      if (response?.success) {
        Toaster.success("Users assigned to folder");
      } else {
        Toaster.error("Failed to assigned to folder."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error assigned to folder:", error);
      Toaster.error("Error assigned to folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Folder Creation
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/create-directory",
        method: "POST",
        data: {
          entity_name: newFolderName,
          entity_description: newFolderDetails,
          parent_directory_id: "",
          directory_owner: user.user_id
        },
      });

      if (response?.success) {

        console.log(response.data);


        if (assignClientToFolder === "yes") {
          if (response.data && typeof response.data === 'object') {
            setSelectedFolders(prevFolders => [...prevFolders, response.data]);
          } else {
            console.warn("Unexpected response format:", response.data);
          }
        }
        setNewFolderName("");
        setNewFolderDetails("");
        setAssignClientToFolder("yes");
        Toaster.success("Folder created successfully!");

        // ✅ Close modal properly
        const modalElement = document.getElementById("createFolder");
        const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
        modalInstance.hide();

        // ✅ Remove lingering backdrop & blur effect
        document.body.classList.remove("modal-open");
        document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
      } else {
        Toaster.error(response?.data?.message || "Failed to create folder.");
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      Toaster.error("Error creating folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // import clients from excel sheets 
  // const handleFileUpload = async (event) => {
  //   const file = event.target.files[0];

  //   if (!file) return;

  //   // Ensure the file is an Excel file
  //   if (!file.name.match(/\.(xls|xlsx)$/)) {
  //     Toaster.error("Invalid file type. Please upload an Excel file.");
  //     return;
  //   }

  //   const formData = new FormData();
  //   formData.append("file", file);

  //   setLoading(true);

  //   try {
  //     const response = await callApi({
  //       endpoint: "/admin/clients/import-clients",
  //       method: "POST",
  //       data: formData,
  //       headers: { "Content-Type": "multipart/form-data" },
  //     });

  //     // Check if the response is successful and handle success
  //     if (response?.success) {
  //       const { inserted = [], skipped = [] } = response.data || {}; // Destructure the inserted and skipped clients

  //       // Handle inserted clients
  //       if (inserted.length > 0) {
  //         Toaster.success(`${inserted.length} clients imported successfully!`);
  //       }

  //       // Handle skipped clients (e.g., duplicates)
  //       if (skipped.length > 0) {
  //         const skippedEmails = skipped.map((client) => client.email).join(', ');
  //         Toaster.error(`${skipped.length} clients were skipped due to duplicates: ${skippedEmails}`);
  //       }

  //       // Optionally, handle further logic if you want to process the inserted/skipped clients
  //       // e.g., update UI state or store errors

  //     } else {
  //       // Handle case where API returns success: false
  //       Toaster.error(response?.message || "Failed to import clients.");
  //     }
  //   } catch (error) {
  //     // Catch network or server errors and show appropriate messages
  //     console.error("Error during import:", error);

  //     // Handle errors when API response is malformed
  //     if (error.response && error.response.data) {
  //       Toaster.error("Error importing clients. Please try again.");
  //     } else {
  //       Toaster.error("Network error. Please try again.");
  //     }
  //   } finally {
  //     setLoading(false); // Turn off the loading state after the request completes
  //   }
  // };

  const [modalVisible, setModalVisible] = useState(false); // Controls modal visibility
  const [errorMessages, setErrorMessages] = useState([]); // Stores error messages
  const [successMessages, setSuccessMessages] = useState([]); // Stores success messages
  const [insertedEmails, setInsertedEmails] = useState([]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    // Ensure the file is an Excel file
    if (!file.name.match(/\.(xls|xlsx)$/)) {
      Toaster.error("Invalid file type. Please upload an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);

    try {
      const response = await callApi({
        endpoint: "/admin/clients/import-clients",
        method: "POST",
        data: formData,
      });

      const { inserted = [], skipped = [] } = response?.data || {}; // Destructure inserted and skipped clients
      console.log("inserted", inserted);
      console.log("skipped", skipped);

      // Check if no new clients were inserted
      if (inserted.length === 0) {
        setErrorMessages(["No new clients inserted. Please check your data."]);
        setModalVisible(true); // Show modal for "No new clients inserted"
      }

      // Handle inserted clients
      if (inserted.length > 0) {
        const insertedEmails = inserted.map((client) => client.user_email).join(', ');
        console.log("sadsadsadsada", insertedEmails);
        setSuccessMessages([`${inserted.length} clients Inserted: ${insertedEmails}`]);
        setModalVisible(true);
      }

      // Handle skipped clients (e.g., duplicates)
      if (skipped.length > 0) {
        const skippedEmails = skipped.map((client) => client.email).join(', ');
        setErrorMessages([`${skipped.length} clients were skipped due to duplicates: ${skippedEmails}`]);
        setModalVisible(true); // Show modal for skipped clients
      }

    } catch (error) {
      // Handle API call failure
      const response = error?.response?.data?.data?.skipped;
      console.log("API Error:", error, response);
      const skippedEmails = response.map((client) => client.email).join(', ');
      setErrorMessages([`${response.length} clients were skipped due to duplicates: ${skippedEmails}`]);
      setModalVisible(true);
    }
    finally {
      setLoading(false); // Turn off the loading state after the request completes
    }
  };

  return (
    <MainLayout>
      <ToastContainerComponent /> {/* ✅ Toast Notifications Container */}
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="global_head_tag">
                  <span><i className="fa-solid fa-user-plus"></i></span> Create New Client
                </div>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="create_new_client_holder">
                  <div className="global_cardbox inner_global_card">
                    <div className="global_card_header d-flex">
                      <div className="global_head_tag"><span>1</span> Basic Info</div>
                      {/* <button className="btn btn-primary orange_btn" type="button">
                        <span><i className="fa-solid fa-user-plus"></i></span> <em>Import Multiple Users With Excel</em>
                      </button> */}
                      <button
                        className="btn btn-primary orange_btn"
                        type="button"
                        onClick={() => document.getElementById("fileInput").click()}
                      >
                        <span><i className="fa-solid fa-user-plus"></i></span>
                        <em>Import Multiple Users With Excel</em>
                      </button>
                      <input
                        type="file"
                        id="fileInput"
                        accept=".xls,.xlsx"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                      />

                    </div>
                    <div className="create_new_client_holder">
                      {basicInfoList.map((info) => (
                        <BasicInfoBox
                          key={info.id}
                          id={info.id}
                          userData={info}
                          onDelete={deleteBasicInfo}
                          onInputChange={handleInputChange}
                          isDeletable={basicInfoList.length > 1}
                          errorMessage={errors[info.user_email]} // ✅ Pass error message
                        />
                      ))}

                      <div className="add_btn_holder d-flex">
                        <button type="button" className="btn btn-primary orange_btn" onClick={addNewBasicInfo}>
                          <span><i className="fa-solid fa-plus"></i></span> Add Another
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ✅ Conditionally render User Settings and Folder Access when only 1 user exists */}
                  {basicInfoList.length === 1 && (
                    <>
                      {/* User Settings Component */}
                      <UserSettings userSettings={userSettings} onSettingsChange={handleUserSettingsChange} />

                      {/* ✅ Give User Access To Folders (Recommended) Section */}
                      <div className="global_cardbox inner_global_card">
                        <div className="global_card_header">
                          <div className="global_head_tag"><span>3</span> Give User Access To Folders <b>(Recommended)</b></div>
                        </div>
                        <div className="user_access_permission">
                          <div className="row">
                            <div className="col-12 col-md-4">
                              <div className="add_user_box">
                                <a href="#!" onClick={openFolderModal}>
                                  <i className="fas fa-folder-open"></i>
                                </a>
                                <div className="assigne_fldr">Assign Folders</div>
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="add_user_box">
                                <a href="#!" onClick={openAddFolderModal}><i className="fa-solid fa-user-plus"></i></a>
                                <div className="assigne_fldr">Add Folder</div>
                              </div>
                            </div>
                          </div>
                          {selectedFolders.length > 0 && (
                            <div className="col-12 assigned-users mt-2">
                              <strong>Assigned Folders:</strong>
                              <ul>
                                {selectedFolders.map((folder) => (
                                  <li key={folder.entity_id}>
                                    {folder.entity_name}
                                    <button onClick={() => removeAssignedFolder(folder.entity_id)}>✖</button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="update_btn_holder">
                    <button type="submit" className="btn btn-primary">Save</button>
                    <button type="button" className="btn btn-primary orange_btn" onClick={() => navigate("/clients")}>
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal Component in Parent */}
      {modalVisible && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload Status</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalVisible(false)} // Close the modal when the close button is clicked
                ></button>
              </div>
              <div className="modal-body scrollable-modal-body" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {/* Display Success Messages */}
                {successMessages.length > 0 && (
                  <div>
                    <h6>Success:</h6>
                    <ul>
                      {successMessages.map((message, index) => (
                        <li key={index}>{message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Display Error Messages */}
                {errorMessages.length > 0 && (
                  <div>
                    <h6>Errors:</h6>
                    <ul>
                      {errorMessages.map((message, index) => (
                        <li key={index}>{message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}



















      {/* ✅ Add People Modal */}
      <div className="modal fade global_modal addpeople_modal" id="addFolderModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content ">
            <div className="modal-header">
              <h5 className="modal-title">Select Folders</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="folderName">Search Folder</label>
                <input type="text" placeholder="Search Folders..." value={searchFolderQuery} onChange={(e) => {
                  setSearchFolderQuery(e.target.value);
                  fetchFolders(e.target.value);
                }}
                />
                {isSearching && <p>Loading folders...</p>}
              </div>


              {/* User Selection */}
              <ul className="search_ppl">
                {allFolders.map((folder) => (
                  <li key={folder.entity_id}>
                    <input type="checkbox" checked={tempSelectedFolders.some((f) => f.entity_id === folder.entity_id)} onChange={() => toggleTempFolderSelection(folder)} />
                    {folder.entity_name}
                  </li>
                ))}
              </ul>

              {/* ✅ Assign Button */}
              <button onClick={handleAssignFolders} className="btn btn-primary">Assign</button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Create Folder Modal */}
      <div className="modal fade global_modal" tabIndex="-1" id="createFolder">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Folder</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="folderName">Name</label>
                  <input
                    type="text"
                    id="folderName"
                    className="form-control"
                    placeholder="Folder Name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="folderDetails">Details</label>
                  <textarea
                    id="folderDetails"
                    placeholder="Details"
                    className="form-control"
                    maxLength="1000"
                    value={newFolderDetails}
                    onChange={(e) => setNewFolderDetails(e.target.value)}
                  />
                  <p>Character Limit: 1000</p>
                </div>

                {/* ✅ Add People to Folder */}
                <div className="form-group">
                  <label>Assign current client to this folder? </label>
                  <div className="chk_holder">
                    <div className="form-check">

                      <input className="form-check-input" type="radio" name="assignFolder" value="yes" checked={assignClientToFolder === "" || assignClientToFolder === "yes"} onChange={() => setAssignClientToFolder("yes")} />
                      <label className="form-check-label" htmlFor="assignFolder">
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="assignFolder" value="yes" onChange={() => setAssignClientToFolder("no")} />
                      <label className="form-check-label" htmlFor="assignFolder">
                        No
                      </label>
                    </div>
                  </div>
                  <span className="note">* Select "Yes" to assign users, otherwise, the folder will be personal.</span>
                </div>

                {/* ✅ Buttons */}
                <div className="btn_holder">
                  <button type="button" className="btn btn-primary" onClick={createFolder}>
                    Submit
                  </button>
                  <button data-bs-dismiss="modal" className="btn btn-primary orange_btn">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );

};

export default CreateClients;





